import React, { useContext, useEffect, useMemo, useState } from 'react'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { Context } from '../context'
import { AnyObject, ContextValue, EditableFieldsOfType, getFieldControlId } from '../types'

/**
 * Password field.
 */
export const DataEditContextTextField = <T extends AnyObject>({
  field,
  maxHintLength,
  multiline,
  type,
  resetError,
  icon,
  id,
  hideLabel,
  insetLabel,
  ...rest
}: {
  field: EditableFieldsOfType<T, string>
  maxHintLength?: number
  multiline?: boolean
  type?: string
  resetError?: () => void
  icon?: React.ReactElement
  id?: string
  hideLabel?: boolean
  insetLabel?: boolean
} & Omit<Record<string, unknown>, 'onChange' | 'value' | 'error' | 'name'>) => {
  const { flatData, errors, disabled, setFields, handleSubmit, localeBase, t } = useContext<ContextValue<T>>(Context)
  const controlId = getFieldControlId(field, id, rest.testId as string | undefined)

  const [value, setValue] = useState<string>(flatData[field] as unknown as string)

  const error = errors[field]

  const setField = useMemo(() => (newValue: string) => setFields({ [field]: newValue }), [setFields, field])

  useEffect(() => {
    setValue(flatData[field] as unknown as string)
  }, [field, flatData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    setField(newValue)
    if (resetError) resetError()
  }

  return (
    <InputField
      id={controlId}
      icon={icon}
      onKeyDown={multiline ? undefined : e => handleSubmit(e)}
      multiline={multiline}
      onChange={handleChange}
      value={value ?? ''}
      error={error}
      disabled={disabled[field]}
      name={field}
      label={!hideLabel ? t(`${localeBase}.${field}`) : undefined}
      type={type}
      insetLabel={insetLabel}
      helperText={
        error ?? (maxHintLength ? `${t(`${localeBase}.charLeft`)} ${maxHintLength - (value ? value.length : 0)}` : null)
      }
      {...rest}
    />
  )
}

/**
 * Number field.
 */
